import { format, subDays, subMonths, startOfMonth } from "date-fns";

import { MetricValueAggregatedResponseDto } from "../libs/cellera-sdk";
import { TimeRange } from "./dateRangeHelpers";

/**
 * Sleep Data Transformation Helpers
 * Helper functions for transforming and processing sleep metric data
 */

/**
 * Sleep stages data structure from API
 * Based on existing pattern in Sleep.tsx (lines 59-71, 84-90)
 */
export interface SleepStagesData {
  rem?: number;
  stage1?: number;
  stage2?: number;
  stage3?: number;
  // Alternative naming conventions
  remSleep?: number;
  deepSleep?: number;
  lightSleep?: number;
  awakeTime?: number;
}

/**
 * Processed sleep stages for chart display
 */
export interface ProcessedSleepStages {
  deep: number;
  rem: number;
  light: number;
  awake: number;
}

/**
 * Transform API data to a map of date -> value for numeric metrics
 */
export const buildDataMap = (
  data: MetricValueAggregatedResponseDto[] | undefined
): Record<string, number> => {
  const dataMap: Record<string, number> = {};
  if (!data) return dataMap;

  for (const d of data) {
    const key = format(new Date(d.startDate), "yyyy-MM-dd");
    dataMap[key] = d.valueIntData?.sum ?? d.valueFloatData?.sum ?? 0;
  }

  return dataMap;
};

/**
 * Transform API data to a map of date -> JSON object (for sleep stages)
 * The JSON data structure matches SleepStagesData interface
 */
export const buildJsonDataMap = (
  data: MetricValueAggregatedResponseDto[] | undefined
): Record<string, SleepStagesData> => {
  const dataMap: Record<string, SleepStagesData> = {};
  if (!data) return dataMap;

  for (const d of data) {
    const key = format(new Date(d.startDate), "yyyy-MM-dd");
    // valueJsonData.sum contains the aggregated JSON object with sleep stage properties
    const jsonValue = d.valueJsonData?.sum ?? d.valueJsonData;
    dataMap[key] = (jsonValue as SleepStagesData) ?? {};
  }

  return dataMap;
};

/**
 * Parse sleep stages from JSON value
 * Follows existing pattern from Sleep.tsx (lines 84-90)
 * Handles different naming conventions: stage1/stage2/stage3/rem vs deepSleep/lightSleep/remSleep/awakeTime
 */
export const parseSleepStages = (
  value: SleepStagesData | null | undefined
): ProcessedSleepStages => {
  if (!value) return { deep: 0, rem: 0, light: 0, awake: 0 };

  return {
    // Deep sleep: stage3 or deepSleep
    deep: value.stage3 ?? value.deepSleep ?? 0,
    // REM sleep: rem or remSleep
    rem: value.rem ?? value.remSleep ?? 0,
    // Light sleep: stage1 + stage2 or lightSleep
    light: value.lightSleep ?? (value.stage1 ?? 0) + (value.stage2 ?? 0),
    // Awake time: awakeTime
    awake: value.awakeTime ?? 0,
  };
};

/**
 * Chart data point for single metric
 */
export interface ChartDataPoint {
  date: string;
  value: number;
}

/**
 * Build chart data points based on time range for a single metric
 */
export const buildChartData = (
  dataMap: Record<string, number>,
  selectedTimeRange: TimeRange,
  selectedDate: Date | null
): ChartDataPoint[] => {
  const today = new Date();

  if (selectedTimeRange === "1D") {
    const key = format(today, "yyyy-MM-dd");
    return [{ date: format(today, "MMM d, yyyy"), value: dataMap[key] ?? 0 }];
  }

  if (selectedTimeRange === "1W") {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(today, i);
      const key = format(date, "yyyy-MM-dd");
      days.push({
        date: format(date, "EEE").slice(0, 2),
        value: dataMap[key] ?? 0,
      });
    }
    return days;
  }

  if (selectedTimeRange === "1M") {
    const days = [];
    for (let i = 30; i >= 0; i--) {
      const date = subDays(today, i);
      const key = format(date, "yyyy-MM-dd");
      days.push({ date: format(date, "dd"), value: dataMap[key] ?? 0 });
    }
    return days;
  }

  if (selectedTimeRange === "6M") {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(today, i);
      const key = format(startOfMonth(date), "yyyy-MM");
      const total = Object.keys(dataMap)
        .filter((d) => d.startsWith(key))
        .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
      months.push({ date: format(date, "MMM"), value: total });
    }
    return months;
  }

  if (selectedTimeRange === "1Y") {
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const date = subMonths(today, i);
      const key = format(startOfMonth(date), "yyyy-MM");
      const total = Object.keys(dataMap)
        .filter((d) => d.startsWith(key))
        .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
      months.push({ date: format(date, "MMM").charAt(0), value: total });
    }
    return months;
  }

  if (selectedTimeRange === "CUSTOM" && selectedDate) {
    const key = format(selectedDate, "yyyy-MM-dd");
    return [
      { date: format(selectedDate, "MMM d, yyyy"), value: dataMap[key] ?? 0 },
    ];
  }

  return [];
};

/**
 * Chart data point for sleep stages (stacked bar chart)
 */
export interface SleepStagesChartDataPoint extends ProcessedSleepStages {
  date: string;
}

/**
 * Build chart data for sleep stages (stacked bar chart)
 */
export const buildSleepStagesChartData = (
  jsonDataMap: Record<string, SleepStagesData>,
  selectedTimeRange: TimeRange,
  selectedDate: Date | null
): SleepStagesChartDataPoint[] => {
  const today = new Date();

  if (selectedTimeRange === "1D") {
    const key = format(today, "yyyy-MM-dd");
    const stages = parseSleepStages(jsonDataMap[key]);
    return [{ date: format(today, "MMM d, yyyy"), ...stages }];
  }

  if (selectedTimeRange === "1W") {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(today, i);
      const key = format(date, "yyyy-MM-dd");
      const stages = parseSleepStages(jsonDataMap[key]);
      days.push({ date: format(date, "EEE").slice(0, 2), ...stages });
    }
    return days;
  }

  if (selectedTimeRange === "1M") {
    const days = [];
    for (let i = 30; i >= 0; i--) {
      const date = subDays(today, i);
      const key = format(date, "yyyy-MM-dd");
      const stages = parseSleepStages(jsonDataMap[key]);
      days.push({ date: format(date, "dd"), ...stages });
    }
    return days;
  }

  if (selectedTimeRange === "6M") {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(today, i);
      const key = format(startOfMonth(date), "yyyy-MM");
      const matchingKeys = Object.keys(jsonDataMap).filter((d) =>
        d.startsWith(key)
      );
      const totals = matchingKeys.reduce(
        (acc, k) => {
          const stages = parseSleepStages(jsonDataMap[k]);
          return {
            deep: acc.deep + (stages.deep ?? 0),
            rem: acc.rem + (stages.rem ?? 0),
            light: acc.light + (stages.light ?? 0),
            awake: acc.awake + (stages.awake ?? 0),
          };
        },
        { deep: 0, rem: 0, light: 0, awake: 0 }
      );
      months.push({ date: format(date, "MMM"), ...totals });
    }
    return months;
  }

  if (selectedTimeRange === "1Y") {
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const date = subMonths(today, i);
      const key = format(startOfMonth(date), "yyyy-MM");
      const matchingKeys = Object.keys(jsonDataMap).filter((d) =>
        d.startsWith(key)
      );
      const totals = matchingKeys.reduce(
        (acc, k) => {
          const stages = parseSleepStages(jsonDataMap[k]);
          return {
            deep: acc.deep + (stages.deep ?? 0),
            rem: acc.rem + (stages.rem ?? 0),
            light: acc.light + (stages.light ?? 0),
            awake: acc.awake + (stages.awake ?? 0),
          };
        },
        { deep: 0, rem: 0, light: 0, awake: 0 }
      );
      months.push({ date: format(date, "MMM").charAt(0), ...totals });
    }
    return months;
  }

  if (selectedTimeRange === "CUSTOM" && selectedDate) {
    const key = format(selectedDate, "yyyy-MM-dd");
    const stages = parseSleepStages(jsonDataMap[key]);
    return [{ date: format(selectedDate, "MMM d, yyyy"), ...stages }];
  }

  return [];
};
